<template>
    <div class="SystemItem" :class="{ collapsed }" @xclick.stop="toggleCollapse" @click.stop="toggleCollapse">
        <div class="title header --global-clickable">
            <JourneyIcon class="journeyIcon" type="theory" :label="(index + 1).toString()" />

            <div class="headerText">
                <p> Journey </p>
                <div class="headerTitle"> {{ journey.name }} </div>
                <div class="text"> Add lifekey text </div>
            </div>

            <div class="buttonToggleCollapse">
                <svgicon class="buttonToggleCollapseIcon" icon="chevron-down" :xstyle="{ fill: theme.color }" />
            </div>
        </div>

        <TransitionExpandCollapse>
            <div v-if="collapsed" class="paddingBox">
                <div class="paddingBoxTopSpacer" />
                <SingleSystemVerticalTimeline
                    :items="journey.steps"
                    extend-top
                    :content-overflow-hidden="false"
                    :line-style="{ dasharray: [2, 2], color: 'rgba(0, 0, 0, 0.1)' }"
                    :active-line-style="{ color: themeColor }"
                >
                    <template slot="bullet" slot-scope="props">
                        <StepBullet
                            :current="props.item.current"
                            :progress="props.item.progress"
                            :icon="props.item.name === 'quiz' ? '' : 'steps-3'"
                        />
                    </template>

                    <template slot="content" slot-scope="props">
                        <SystemQuiz v-if="props.item.name === 'quiz'" :current="false" />
                        <SystemIntro v-else-if="props.item.name === 'intro'" :current="false" />
                        <SystemStepItem
                            v-else
                            :system-id="systemId"
                            :journey="journey"
                            :step="props.item"
                            :index="props.index"
                            :group-name="groupName"
                            :has-system-games="hasSystemGames"
                        />
                    </template>
                </SingleSystemVerticalTimeline>
            </div>
        </TransitionExpandCollapse>
    </div>
</template>

<script>
import { getStepGroupTitle } from '@/utils';
import { cssVariables } from '@/category-themes';

import SingleSystemVerticalTimeline from './SingleSystemVerticalTimeline';
import TransitionExpandCollapse from '@/components/TransitionExpandCollapse';
import SystemStepItem from './SystemStepItem';
import SystemQuiz from './SystemQuiz';
import StepBullet from '@/modules/course/components/StepBullet';
import JourneyIcon from '@/modules/course/components/JourneyIcon';
import SystemIntro from './SystemIntro';

export default {
    components: {
        TransitionExpandCollapse,
        SingleSystemVerticalTimeline,
        StepBullet,
        SystemStepItem,
        SystemQuiz,
        JourneyIcon,
        SystemIntro
    },

    inject: ['theme'],

    props: {
        journey: {
            type: Object,
            required: true,
            default: () => {}
        },

        groupName: {
            type: String,
            required: false,
            default: ''
        },

        index: {
            type: Number,
            required: true,
            default: 0
        },

        isSingleJourney: {
            type: Boolean,
            required: false,
            default: false
        },

        hasSystemGames: {
            type: Boolean,
            required: false,
            default: true
        },

        systemId: {
            type: String,
            required: true,
            default: ''
        }
    },

    data() {
        return {
            collapsed: false
        };
    },

    computed: {
        themeColor() {
            return cssVariables.completionColor;
        }
    },

    mounted() {
        this.setDefaultCollapsedValue();
    },

    methods: {
        toggleCollapse() {
            this.collapsed = !this.collapsed;
        },

        setDefaultCollapsedValue() {
            this.isSingleJourney ? (this.collapsed = true) : (this.collapsed = false);
        }
    }
};
</script>

<style lang="scss" scoped>
$bulletColumnWidth: 4em;

.SystemItem {
    background-color: white;
    box-shadow: 0px 2px 23px 3px rgba(79, 79, 79, 0.1);
    min-width: 39em;
    margin-top: 1em;
    border-bottom: 1px solid rgba(133, 133, 133, 0.12);

    &:last-of-type {
        margin-bottom: 1em;
    }

    &.collapsed {
        .buttonToggleCollapseIcon {
            transform: rotate(0deg);
        }
    }

    /deep/ .Step {
        .bullet {
            width: 95px;
            margin-right: 2em;
            @media (max-width: 530px) {
                width: 4em;
            }
        }
    }
}

.header {
    display: flex;
    align-items: center;
}

.headerText {
    flex: 1;
    padding-right: 20px;
    p {
        font-size: 16px;
        line-height: 22px;
        color: #727272;
        margin: 5px 0;
        @media (max-width: 530px) {
            font-size: 14px;
        }
    }
}

.headerTitle {
    font-size: 140%;
    font-weight: 600;
    margin-top: 0;
    @media (max-width: 530px) {
        font-size: 16px;
    }
}

.title {
    width: 100%;
    padding: 1em 0;
}

.journeyIcon {
    width: 96px;
    height: 96px;
    margin: 1.25em 1.25em 1.25em 0;
    @media (max-width: 530px) {
        width: 66px;
        height: 66px;
    }
}

.buttonToggleCollapse {
    width: 35px;
    height: 35px;
    background-color: rgba(black, 0.03);
    border: 1px solid rgba(black, 0.02);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    &:hover {
        border: 1px solid $purple;
    }
    @media (max-width: 1000px) {
        display: none;
    }
}

.buttonToggleCollapseIcon {
    width: 12px;
    height: 12px;
    fill: $completionColor;
    transition: transform 0.5s;
    transform: rotate(-180deg);
}

.SystemItem {
    $leftSidePadding: 1.5em;
    border-radius: 4px;
    .title {
        padding: 0 1.25em;
        width: 100%;
        @media (max-width: 530px) {
            padding: 0 1em;
        }
    }
    .SingleSystemVerticalTimeline {
        /deep/ .extendedTopLine {
            height: 2.5em;
            @media (max-width: 530px) {
                height: 4.5em;
            }
            @media (max-width: 410px) {
                height: 5.2em;
            }
        }
    }
}

.progress-bar-wrapper {
    margin-top: 7.5px;
    background: #eaeaea;
    .progress-bar {
        width: 0;
        height: 3px;
        background-color: #5983f7;
        border-top-left-radius: 50% 50px;
        border-bottom-left-radius: 50% 50px;
        border-top-right-radius: 50% 50px;
        border-bottom-right-radius: 50% 50px;
    }
}

.label {
    font-weight: 600;
}

.text {
    font-size: 16px;
    line-height: 22px;
    color: #727272;
    margin-top: 10px;
    @media (max-width: 530px) {
        font-size: 14px;
    }
}

.paddingBox {
    // NOTE: needed to avoid cutting shadow while collapsing
    padding: 0 1.25em;
    @media (max-width: 530px) {
        padding: 0 1em;
    }
    background: white;
}

.paddingBoxTopSpacer,
.paddingBoxBottomSpacer {
    height: 1em;
}
</style>
